import type {
	PortalCatalogFacetedResponseDto,
	PortalCatalogItemDetailResponseDto,
} from '@supply-mover-2-0/shared';
import type { QueryClient } from '@tanstack/react-query';

/**
 * React Query key prefixes for the storefront's catalog data.
 *
 * The queries build their keys from these prefixes and the invalidations
 * target the same constants. When the catalog query moved to the faceted
 * endpoint, its key changed from ['catalog', 'items'] while the favorite
 * star and the stale-image refetch kept invalidating the old key, which no
 * longer matched anything, so a toggled star only showed after a page
 * refresh (PD-16543). Sharing one constant keeps a future rename from
 * silently orphaning an invalidation again.
 */

/** Every catalog result page, for any filter, sort and page combination. */
export const CATALOG_FACETED_QUERY_KEY = ['catalog', 'faceted'] as const;

/** Every product detail page. */
export const CATALOG_ITEM_QUERY_KEY = ['catalog', 'item'] as const;

/** The shopper's Favorites page. */
export const FAVORITES_QUERY_KEY = ['favorites'] as const;

/**
 * The cached queries that carry an item's favorite flag. A toggle on any
 * surface refreshes all of them, so the catalog grid and list, the product
 * page and the Favorites page agree once the shopper navigates between them.
 */
const FAVORITE_BEARING_QUERY_KEYS = [
	CATALOG_FACETED_QUERY_KEY,
	CATALOG_ITEM_QUERY_KEY,
	FAVORITES_QUERY_KEY,
] as const;

/** Mark every favorite-bearing query stale (active ones refetch at once). */
export async function invalidateFavoriteQueries(
	queryClient: QueryClient,
): Promise<void> {
	await Promise.all(
		FAVORITE_BEARING_QUERY_KEYS.map((queryKey) =>
			queryClient.invalidateQueries({ queryKey }),
		),
	);
}

/**
 * Write a saved favorite flag straight into the cached catalog pages and the
 * item's product page, before those queries refetch.
 *
 * A background refetch alone leaves the old flag on screen until the
 * faceted search answers, which takes a few seconds on a large catalog: a
 * shopper who starred an item on its product page and went back saw the
 * card's star empty, then fill in later. The refetch still follows and
 * stays the source of truth (it also drops an unstarred item from a
 * Favorites-filtered page).
 */
export function applyFavoriteToCachedQueries(
	queryClient: QueryClient,
	itemId: number,
	isFavorite: boolean,
): void {
	queryClient.setQueriesData<PortalCatalogFacetedResponseDto>(
		{ queryKey: CATALOG_FACETED_QUERY_KEY },
		(old) => {
			if (old?.data.some((item) => item.id === itemId) !== true) {
				return old;
			}

			return {
				...old,
				data: old.data.map((item) =>
					item.id === itemId ? { ...item, isFavorite } : item,
				),
			};
		},
	);
	queryClient.setQueriesData<PortalCatalogItemDetailResponseDto>(
		{ queryKey: [...CATALOG_ITEM_QUERY_KEY, itemId] },
		(old) => {
			if (old?.data == null) {
				return old;
			}

			return { ...old, data: { ...old.data, isFavorite } };
		},
	);
}
